/**
 * /meetings/new — the paste-and-metadata form.
 *
 * User fills in metadata + pastes raw input + clicks the submit button;
 * POST /meetings creates the draft, then POST /meetings/:id/transform-stream
 * runs Claude with SSE progress events feeding a deterministic progress
 * bar. When Claude finishes we navigate to /meetings/:id/edit.
 *
 * Styled to match the Studio dashboard aesthetic: cloud-surfaced cards
 * with a thin charcoal outline, pine-filled pills, staggered fade-in on
 * mount.
 */
import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays } from 'lucide-react';
import { api, ApiError, postAndStream } from '../lib/apiClient.ts';
import { ProjectPicker } from '../components/editor/ProjectPicker.tsx';
import { TokenMeter } from '../components/editor/TokenMeter.tsx';
import {
  TransformProgressBar,
  phaseBaseProgress,
  writingProgressFromBytes,
  type TransformPhase,
} from '../components/editor/TransformProgressBar.tsx';
import {
  CalendarEventPicker,
  type CalendarEvent,
} from '../components/editor/CalendarEventPicker.tsx';
import { ProjectSuggestionDialog } from '../components/editor/ProjectSuggestionDialog.tsx';
import {
  NewMeetingAttendeesField,
  type AttendeeEmailEntry,
} from '../components/editor/NewMeetingAttendeesField.tsx';
import { useOjbTeamRoster } from '../hooks/useOjbTeamRoster.ts';
import { estimateTokens, TOKEN_THRESHOLDS } from '../lib/tokenCount.ts';
import { NO_AUTOFILL } from '../lib/noAutofill.ts';
import { getLocalDateString } from '../lib/dateHelpers.ts';
import { cleanCalendarSubject } from '../lib/cleanCalendarSubject.ts';
import { useTour } from '../lib/useTour.ts';
import {
  suggestProjectForSubject,
  type ProjectMatchCandidate,
} from '../lib/fuzzyProjectMatch.ts';

type InputSource = 'transcript' | 'ai_summary' | 'freeform';
type MeetingFormat = 'in_person' | 'video' | 'phone' | 'hybrid';

const INPUT_MODES: { value: InputSource; label: string; hint: string }[] = [
  { value: 'ai_summary', label: 'AI notes', hint: 'Already-summarized notes from Granola, Fireflies, etc.' },
  { value: 'transcript', label: 'Transcript', hint: 'Paste raw dialogue from Teams, Zoom, etc.' },
  { value: 'freeform', label: 'Freeform', hint: 'Handwritten or typed shorthand notes.' },
];

const FORMATS: { value: MeetingFormat; label: string }[] = [
  { value: 'video', label: 'Video (Teams/Zoom)' },
  { value: 'in_person', label: 'In person' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'phone', label: 'Phone' },
];

// phaseBaseProgress + writingProgressFromBytes are shared helpers
// imported from TransformProgressBar — both NewMeeting and Editor
// use the same mapping for a consistent progress-bar feel.

export function NewMeetingRoute() {
  const navigate = useNavigate();
  const today = getLocalDateString();

  useTour({
    pageKey: 'new-meeting',
    steps: [
      {
        id: 'new-calendar',
        title: 'Start from a calendar event',
        text: 'Pull a meeting from your Outlook calendar. Title, date, format, and attendees prefill in one click \u2014 and OJB attendees auto-fill with their full title and office.',
        attachTo: '[data-tour="new-calendar-picker"]',
        on: 'bottom',
      },
      {
        id: 'new-paste',
        title: 'Or paste your notes',
        text: 'Drop in a transcript, an AI summary from Granola or Fireflies, or your own freeform shorthand. Mix sources for richer context \u2014 the token counter tells you how big it\u2019s getting.',
        attachTo: '[data-tour="new-paste-panel"]',
        on: 'top',
      },
      {
        id: 'new-submit',
        title: 'Transform when ready',
        text: 'Hit this when you’re ready. Claude streams the structured minutes back live, then flags anything ambiguous so you can resolve it in the editor.',
        attachTo: '[data-tour="new-submit"]',
        on: 'top',
      },
    ],
  });

  const [title, setTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState(today);
  const [project, setProject] = useState<{ projectId?: string; projectName?: string; label: string }>({
    label: '',
  });
  // Optional display-only override for the project name shown in
  // the rendered minutes. The meeting still files under the real
  // OJB project (via project.projectId) — alias only affects what
  // external recipients see on the document. Empty = no override.
  const [projectAlias, setProjectAlias] = useState('');
  const [meetingFormat, setMeetingFormat] = useState<MeetingFormat>('video');
  const [location, setLocation] = useState('');

  interface SourceBlock {
    kind: InputSource;
    content: string;
  }
  const [sources, setSources] = useState<SourceBlock[]>([
    { kind: 'ai_summary', content: '' },
  ]);
  const [status, setStatus] = useState<'idle' | 'creating' | 'transforming'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Progress-bar state. `phase` drives the label + base position; byte
  // deltas from the 'writing' phase interpolate the bar smoothly. Kept
  // in state so CSS `transition: width` animates between updates.
  const [phase, setPhase] = useState<TransformPhase>('creating');
  const [progress, setProgress] = useState(0);
  // Keep the highest progress value we've seen so jitter in incoming
  // deltas never moves the bar backwards.
  const maxProgressRef = useRef(0);

  // AttendeeEmailEntry shape comes from NewMeetingAttendeesField so
  // the form's state matches the picker's expectations exactly.
  // Calendar prefill writes here too via prefillFromEvent below.
  const [attendeeEmails, setAttendeeEmails] = useState<AttendeeEmailEntry[]>([]);

  // Template variant is intentionally hard-coded for the pilot. The
  // distinction between client_meeting and internal is hidden across
  // the entire surface (form, editor, exports). Re-introduce useState
  // here if/when the variant choice comes back as a user-facing option.
  const templateVariant = 'client_meeting' as const;

  const [graphEventId, setGraphEventId] = useState<string | null>(null);
  /** Microsoft Graph seriesMasterId captured from a recurring calendar
   *  prefill. Sent to the server so future transforms on this series
   *  get weight-boosted project memory. */
  const [seriesMasterId, setSeriesMasterId] = useState<string | null>(null);
  /** Per-meeting opt-in for project-memory injection during transform.
   *  Default ON so the feature is active; user flips OFF for kickoffs
   *  or standalone meetings where prior context would mislead. */
  const [useProjectHistory, setUseProjectHistory] = useState(true);
  /** Banner shown after calendar prefill when we auto-defaulted the
   *  project from a prior meeting in the same series. Dismissable.
   *  Variant tracking removed — pilot has a single unified format. */
  const [seriesAutoDefault, setSeriesAutoDefault] = useState<{
    projectName: string;
  } | null>(null);

  const [projectSuggestion, setProjectSuggestion] = useState<{
    subject: string;
    match: ProjectMatchCandidate;
  } | null>(null);

  const projectsQuery = useQuery({
    queryKey: ['projects', 'all', 'for-match'],
    queryFn: () =>
      api.get<{ projects: Array<{ id: string; name: string }> }>('/projects', {
        limit: 500,
      }),
    staleTime: 5 * 60 * 1000,
  });

  // Roster lookup so calendar prefill can rewrite OJB attendees'
  // names to the roster's authoritative `fullName` (which carries
  // credentials like "Andrew Cridlin, ASLA"). Outlook displayName
  // typically lacks credentials, so without this merge the form
  // shows "Andrew Cridlin" while the OJB picker on the same page
  // would show "Andrew Cridlin, ASLA". Same hook is used inside
  // NewMeetingAttendeesField for a defensive re-lookup at pill
  // render — covers the race where the user picks a calendar event
  // before the roster query has resolved.
  const { members: roster } = useOjbTeamRoster();

  async function prefillFromEvent(e: CalendarEvent) {
    const cleanedTitle = cleanCalendarSubject(e.subject);
    setTitle(cleanedTitle);
    setMeetingDate(e.localDate);
    if (e.location) setLocation(e.location);
    setMeetingFormat(e.isOnlineMeeting ? 'video' : 'in_person');
    setGraphEventId(e.id);
    setSeriesMasterId(e.seriesMasterId ?? null);
    // Clear any stale auto-default banner from a prior picker cycle.
    setSeriesAutoDefault(null);

    const seen = new Set<string>();
    const captured: AttendeeEmailEntry[] = [];
    const allParticipants = [
      ...(e.organizer ? [e.organizer] : []),
      ...e.attendees.map((a) => ({ name: a.name, email: a.email })),
    ];
    for (const p of allParticipants) {
      const email = p.email?.toLowerCase().trim();
      if (!email || seen.has(email)) continue;
      seen.add(email);
      const isOjb = email.endsWith('@ojb.com');
      // For OJB attendees, prefer the roster's fullName over the
      // Outlook displayName so credentials come through ("Andrew
      // Cridlin, ASLA" not "Andrew Cridlin"). Match by email which
      // is the most reliable join key — names drift, capitalization
      // varies, but OJB email addresses are stable.
      let name = p.name || email.split('@')[0];
      if (isOjb) {
        const rosterMatch = roster.find(
          (r) => r.email && r.email.toLowerCase() === email,
        );
        if (rosterMatch?.fullName) {
          name = rosterMatch.fullName;
        }
      }
      captured.push({ name, email, isOjb });
    }
    setAttendeeEmails(captured);

    // Series-memory auto-default: if this calendar event is part of a
    // recurring series and the series has been used before in this
    // tool, prefill the project from the most recent prior meeting
    // in that series. (Template variant auto-default removed for the
    // pilot — single unified format.)
    let seriesApplied = false;
    if (e.seriesMasterId) {
      try {
        const lookup = await api.get<{
          prior: {
            id: string;
            projectId: string;
            projectName: string | null;
          } | null;
        }>('/meetings/series-lookup', { seriesMasterId: e.seriesMasterId });
        if (lookup.prior && lookup.prior.projectName) {
          setProject({
            projectId: lookup.prior.projectId,
            projectName: lookup.prior.projectName,
            label: lookup.prior.projectName,
          });
          setSeriesAutoDefault({
            projectName: lookup.prior.projectName,
          });
          seriesApplied = true;
        }
      } catch {
        // Fall through to fuzzy-match path below if the lookup 500s.
      }
    }

    // Fall back to the fuzzy-subject-to-project matcher when the
    // series lookup didn't apply. (Won't overwrite a series-applied
    // project because seriesApplied short-circuits the dialog.)
    if (!seriesApplied) {
      const projects = projectsQuery.data?.projects ?? [];
      const match = suggestProjectForSubject(cleanedTitle, projects, 0.3);
      if (match) {
        setProjectSuggestion({ subject: cleanedTitle, match });
      }
    }
  }

  const rawInput = useMemo(
    () =>
      sources
        .filter((s) => s.content.trim().length > 0)
        .map((s) => `## ${s.kind.toUpperCase()}\n\n${s.content.trim()}`)
        .join('\n\n'),
    [sources],
  );
  const validSources = sources.filter((s) => s.content.trim().length > 0);
  const inputSource: InputSource =
    validSources.find((s) => s.kind === 'transcript')?.kind ??
    validSources[0]?.kind ??
    'ai_summary';

  const tokens = useMemo(() => estimateTokens(rawInput), [rawInput]);
  const overLimit = tokens >= TOKEN_THRESHOLDS.HARD_CAP;
  const busy = status !== 'idle';

  function bumpProgress(next: number) {
    const clamped = Math.max(maxProgressRef.current, Math.min(100, next));
    maxProgressRef.current = clamped;
    setProgress(clamped);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setWarning(null);

    if (!project.projectId && !project.projectName) {
      setError('Project is required');
      return;
    }
    if (overLimit) {
      setError('Input exceeds the hard token limit - shorten before creating');
      return;
    }

    maxProgressRef.current = 0;
    setProgress(0);
    setPhase('creating');
    setStatus('creating');

    let createdId: string;
    try {
      bumpProgress(5);
      const created = await api.post<{ id: string }>('/meetings', {
        title: title.trim(),
        meetingDate,
        notesDate: today,
        projectId: project.projectId,
        projectName: project.projectId ? undefined : project.projectName,
        projectAlias: projectAlias.trim() || undefined,
        meetingFormat,
        location: location.trim() || undefined,
        inputSource,
        rawInput,
        templateVariant,
        graphEventId: graphEventId ?? undefined,
        seriesMasterId: seriesMasterId ?? undefined,
        // Only send when the user flipped it off — server defaults ON.
        useProjectHistory: useProjectHistory ? undefined : false,
        sources: validSources.length >= 2 ? validSources : undefined,
        attendeeEmails: attendeeEmails.length > 0 ? attendeeEmails : undefined,
      });
      createdId = created.id;
      bumpProgress(phaseBaseProgress('analyzing'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Create failed');
      setStatus('idle');
      return;
    }

    // If there's raw input, stream the transform inline so the user
    // lands on a ready document. Progress bar is driven by SSE events.
    if (rawInput.trim().length > 0) {
      setStatus('transforming');
      try {
        await postAndStream(
          `/meetings/${createdId}/transform-stream`,
          {},
          (eventName, data) => {
            switch (eventName) {
              case 'analyzing':
                setPhase('analyzing');
                bumpProgress(phaseBaseProgress('analyzing'));
                break;
              case 'writing':
                setPhase('writing');
                bumpProgress(phaseBaseProgress('writing'));
                break;
              case 'delta': {
                const bytes =
                  typeof data === 'object' && data !== null && 'bytes' in data
                    ? Number((data as { bytes: unknown }).bytes) || 0
                    : 0;
                bumpProgress(writingProgressFromBytes(bytes));
                break;
              }
              case 'validating':
                setPhase('validating');
                bumpProgress(phaseBaseProgress('validating'));
                break;
              case 'done':
                setPhase('done');
                bumpProgress(100);
                break;
              case 'error': {
                const message =
                  typeof data === 'object' && data !== null && 'message' in data
                    ? String((data as { message: unknown }).message)
                    : 'Transform failed';
                throw new Error(message);
              }
              default:
                // Ignore unknown event names — lets us add events later
                // without breaking old clients.
                break;
            }
          },
        );
      } catch (err) {
        setWarning(
          err instanceof ApiError
            ? `Draft saved but transform failed: ${err.message}. You can retry from the editor.`
            : err instanceof Error
              ? `Draft saved but transform failed: ${err.message}. You can retry from the editor.`
              : 'Draft saved but transform failed. You can retry from the editor.',
        );
      }
    }

    navigate(`/meetings/${createdId}/edit`);
  }

  // Stagger delays for the three main blocks on mount — matches the
  // dashboard's wave-in (0/80/160ms).
  const fadeDelay = (ms: number) => ({ animationDelay: `${ms}ms` });

  return (
    // Mockup: shell--narrow = max-width: 920px; padding: 24px 40px 80px.
    // pt-6 + pb-20 + px-10 is close enough; max-w-4xl (896px) sits a
    // hair narrower but reads identically against the cloud page bg.
    <div className="mx-auto max-w-4xl px-10 pb-20 pt-6">
      <CalendarEventPicker
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={prefillFromEvent}
      />

      <ProjectSuggestionDialog
        isOpen={!!projectSuggestion}
        subject={projectSuggestion?.subject ?? ''}
        suggestion={projectSuggestion?.match ?? null}
        onAccept={(id, name) => {
          setProject({ projectId: id, projectName: name, label: name });
          setProjectSuggestion(null);
        }}
        onDismiss={() => setProjectSuggestion(null)}
      />

      {/* ─── Header + primary calendar CTA ─────────────────────────── */}
      <header
        className="motion-safe:animate-fade-in-up mb-6 pt-4"
        style={fadeDelay(0)}
      >
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-pine">
          New meeting
        </div>
        {/* H1 sized to mockup .nm-header h1: 36px Aeonik 400 with
            tight tracking. font-normal (not semibold) keeps the
            page reading as a quiet "fill out a form" surface, not
            a CTA-heavy hero. */}
        <h1 className="mt-2 text-[36px] font-normal leading-[1.05] tracking-tight text-charcoal">
          Start from notes
        </h1>
        <p className="mt-3 max-w-[64ch] text-[15px] font-light leading-relaxed text-charcoal/80">
          Paste a transcript, AI notes, or your freeform shorthand. Claude
          structures your notes into OJB minutes - you land on the editor
          ready to polish, rewrite, or tweak.
        </p>

        <div className="mt-5 flex flex-wrap items-center gap-4">
          {/* Calendar pulse CTA — mockup .nm-cta: pine pill, padding
              10/18px, 13px medium, charcoal/20 border (subtle inner
              ring on the pine fill so the pill doesn't bloom into
              the bg). */}
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            data-tour="new-calendar-picker"
            className="motion-safe:animate-pulse-cta inline-flex items-center gap-2 rounded-full border border-charcoal/20 bg-pine px-[18px] py-2.5 text-[13px] font-medium text-cloud shadow-md transition-all hover:-translate-y-px hover:bg-pine-deep"
            title="Pick a meeting from your Outlook calendar to prefill title, date, format, and location."
          >
            <CalendarDays size={16} strokeWidth={2.25} />
            Pick from calendar
          </button>
          <span className="text-[12px] text-charcoal/65">
            Prefills title, date, attendees, and format from Outlook.
          </span>
        </div>

        {/* Series auto-default banner — rendered only right after a
            calendar prefill where the series matched a prior meeting.
            Dismissable via the X; disappears on its own when the user
            picks a different project or opens the picker again. */}
        {seriesAutoDefault && (
          // Series banner — mockup .series-banner: rounded-2xl(16px),
          // pine/25 border, pine-soft bg, pine text. Recycle icon
          // glyph on the left grounds the message in a "this is a
          // re-use moment" visual.
          <div className="mt-4 flex items-center gap-3 rounded-2xl border border-pine/25 bg-pine/[0.06] px-[18px] py-3 text-[13px] text-pine">
            <span aria-hidden className="text-lg leading-none">↻</span>
            <div className="flex-1">
              <span className="font-semibold">Recurring series recognized.</span>{' '}
              Auto-filled project{' '}
              <span className="font-semibold">
                &ldquo;{seriesAutoDefault.projectName}&rdquo;
              </span>{' '}
              from a prior instance. Change it if it&apos;s wrong.
            </div>
            <button
              type="button"
              onClick={() => setSeriesAutoDefault(null)}
              aria-label="Dismiss"
              className="shrink-0 rounded-full p-1 text-base text-pine/55 transition-colors hover:bg-pine/10 hover:text-pine"
            >
              ✕
            </button>
          </div>
        )}
      </header>

      <form onSubmit={onSubmit} className="space-y-6">
        {/* ─── Metadata ──────────────────────────────────────────── */}
        <section
          // Mockup .nm-section: cloud + paper-grain texture, charcoal/10
          // border, rounded-[20px], padding 24/28px (px-7 py-6). Paper
          // grain ties the surface to the dashboard / project section
          // chrome family.
          className="paper-grain motion-safe:animate-fade-in-up relative overflow-hidden rounded-[20px] border border-charcoal/10 bg-cloud px-7 py-6 shadow-sm"
          style={fadeDelay(80)}
        >
          <h2 className="relative mb-[18px] text-[12px] font-bold uppercase tracking-[0.18em] text-pine">
            Meeting details
          </h2>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Title" required>
              <input
                {...NO_AUTOFILL}
                type="text"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Duke Park Client Review"
                className="w-full rounded-[10px] border border-charcoal/10 bg-bone px-3.5 py-2.5 text-[14px] text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
              />
            </Field>

            <Field label="Meeting date" required>
              <input
                {...NO_AUTOFILL}
                type="date"
                required
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
                className="w-full rounded-lg border border-charcoal/20 bg-paper-light px-3 py-2 text-sm text-charcoal focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20"
              />
            </Field>

            <Field label="Project" required>
              <ProjectPicker value={project.label} onChange={setProject} required />
            </Field>

            <Field label="Project alias (display only)">
              <input
                {...NO_AUTOFILL}
                type="text"
                value={projectAlias}
                onChange={(e) => setProjectAlias(e.target.value)}
                placeholder={
                  project.projectName
                    ? `Defaults to "${project.projectName}"`
                    : 'Optional - shown on the document instead of the OJB project name'
                }
                className="w-full rounded-[10px] border border-charcoal/10 bg-bone px-3.5 py-2.5 text-[14px] text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
              />
              <span className="mt-1.5 block text-[11px] italic text-charcoal/60">
                Leave blank to show the real project name. Notes still
                file under {project.projectName ? `"${project.projectName}"` : 'the selected project'} regardless.
              </span>
            </Field>

            <Field label="Format">
              <select
                value={meetingFormat}
                onChange={(e) => setMeetingFormat(e.target.value as MeetingFormat)}
                className="w-full rounded-lg border border-charcoal/20 bg-paper-light px-3 py-2 text-sm text-charcoal focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20"
              >
                {FORMATS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Location" className="md:col-span-2">
              <input
                {...NO_AUTOFILL}
                type="text"
                // Password managers sometimes ignore the opt-out hints when
                // the field name literally matches "location" / "address";
                // a neutral name hides this field from heuristic detection.
                name="meeting-venue"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Virtual / Houston office / client site"
                className="w-full rounded-[10px] border border-charcoal/10 bg-bone px-3.5 py-2.5 text-[14px] text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
              />
            </Field>

            {/* NOT a <Field> — that wrapper is a <label>, and per
                HTML spec, clicking anywhere inside a label with no
                explicit `for` attribute dispatches a synthetic click
                on the first labelable descendant. The first such
                element here is the X button on the first OJB pill,
                which means clicking ANY dead space in the attendee
                area was silently removing OJB attendees one by one.
                Use a plain div with a visually-matched label instead. */}
            <div className="block md:col-span-2">
              <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.22em] text-pine/85">
                Attendees
              </span>
              <NewMeetingAttendeesField
                value={attendeeEmails}
                onChange={setAttendeeEmails}
              />
            </div>
          </div>
        </section>

        {/* ─── Paste panel ───────────────────────────────────────── */}
        <section
          data-tour="new-paste-panel"
          className="paper-grain motion-safe:animate-fade-in-up relative overflow-hidden rounded-[20px] border border-charcoal/10 bg-cloud px-7 py-6 shadow-sm"
          style={fadeDelay(160)}
        >
          <div className="relative mb-[18px] flex items-baseline justify-between gap-3">
            <h2 className="text-[12px] font-bold uppercase tracking-[0.18em] text-pine">
              Paste your notes
            </h2>
            <span className="text-[12px] text-charcoal/65">
              One source, or add a second - Claude weaves them together.
            </span>
          </div>

          <div className="relative mt-4 space-y-3">
            {sources.map((src, i) => (
              // Mockup .source-block: bone surface, charcoal/12 border,
              // rounded-2xl(16px), padding 16px. Bone (vs cloud) is one
              // step lighter so the source blocks visually "lift" out
              // of the cloud section card.
              <div
                key={i}
                className="rounded-2xl border border-charcoal/10 bg-bone p-4"
              >
                <div className="mb-2.5 flex flex-wrap items-center gap-1.5">
                  {INPUT_MODES.map((m) => (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() =>
                        setSources((arr) => {
                          const next = [...arr];
                          next[i] = { ...next[i], kind: m.value };
                          return next;
                        })
                      }
                      // Idle: cream pill w/ charcoal hairline. Hover:
                      // pine border + pine text. Active: pine fill.
                      // Smaller padding than the dashboard pills since
                      // these sit inside an already-bordered block.
                      className={
                        'rounded-full border px-3 py-1 text-[12px] font-medium transition-colors ' +
                        (src.kind === m.value
                          ? 'border-pine bg-pine text-cloud'
                          : 'border-charcoal/15 bg-cream text-charcoal hover:border-pine hover:text-pine')
                      }
                    >
                      {m.label}
                    </button>
                  ))}
                  {sources.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setSources((arr) => arr.filter((_, idx) => idx !== i))
                      }
                      className="ml-auto text-[12px] text-charcoal/55 transition-colors hover:text-terra-cotta"
                      aria-label="Remove this source"
                      title="Remove this source"
                    >
                      ✕ Remove
                    </button>
                  )}
                </div>
                <p className="mb-2 text-[11px] italic text-charcoal/60">
                  {INPUT_MODES.find((m) => m.value === src.kind)?.hint}
                </p>
                <textarea
                  {...NO_AUTOFILL}
                  value={src.content}
                  onChange={(e) =>
                    setSources((arr) => {
                      const next = [...arr];
                      next[i] = { ...next[i], content: e.target.value };
                      return next;
                    })
                  }
                  placeholder={
                    INPUT_MODES.find((m) => m.value === src.kind)?.hint ?? ''
                  }
                  rows={12}
                  // Mockup .source-textarea: cream bg, charcoal/10
                  // border, rounded-lg(10px), 13px Aeonik 400, line
                  // 1.55, min-h-[220px]. Cream is the lightest of
                  // the warm-neutral stack so the writing surface
                  // contrasts with the bone source-block bg.
                  className="min-h-[220px] w-full resize-y rounded-[10px] border border-charcoal/10 bg-cream px-3.5 py-3 text-[13px] leading-[1.55] text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
                />
              </div>
            ))}

            {sources.length < 3 && (
              <button
                type="button"
                onClick={() =>
                  setSources((arr) => [
                    ...arr,
                    {
                      kind:
                        (['freeform', 'transcript', 'ai_summary'] as const).find(
                          (k) => !arr.some((s) => s.kind === k),
                        ) ?? 'freeform',
                      content: '',
                    },
                  ])
                }
                className="rounded-full bg-pine px-3 py-1.5 text-[12px] font-medium text-cloud transition-colors hover:bg-pine-deep"
              >
                + Add another source
              </button>
            )}
          </div>

          <div className="mt-4">
            <TokenMeter tokens={tokens} />
          </div>
        </section>

        {/* ─── Errors / warnings / progress ──────────────────────── */}
        {/* Error: terra-cotta tinted banner so destructive / blocking
            messages stay on-brand vs the generic red alert chrome. */}
        {error && (
          <div className="rounded-2xl border border-terra-cotta/30 bg-terra-cotta/10 px-4 py-3 text-[13px] text-terra-cotta">
            {error}
          </div>
        )}
        {/* Warning: amber. Used for "draft saved but transform failed"
            — the underlying create succeeded so this is a soft warn,
            not a blocker. Kept yellow (rather than terra-cotta) so
            the user can distinguish "you can keep going" from "this
            is broken". */}
        {warning && (
          <div className="rounded-2xl border border-amber-300/60 bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
            {warning}
          </div>
        )}

        {busy && (
          <TransformProgressBar phase={phase} progress={progress} />
        )}

        {/* ─── Transform options + Submit ─────────────────────────── */}
        <div className="flex items-center justify-between gap-4 px-1 pt-2">
          {/* Project-history toggle. Default ON; flip OFF for kickoffs
              or genuinely standalone meetings where prior context
              would mislead Claude. Small + unobtrusive on the left so
              the submit button keeps visual primacy on the right. */}
          <label className="flex cursor-pointer select-none items-center gap-2 text-[13px] text-charcoal">
            <input
              type="checkbox"
              checked={useProjectHistory}
              onChange={(e) => setUseProjectHistory(e.target.checked)}
              disabled={busy}
              className="h-4 w-4 accent-pine"
            />
            <span>
              Use project history
              <span
                className="ml-1.5 cursor-help text-charcoal/45"
                title="When on, Claude sees the last few finalized meetings for this project and uses them to match vocabulary + recognize recurring attendees. Turn off for kickoffs or genuinely standalone meetings."
              >
                ⓘ
              </span>
            </span>
          </label>

          <div className="flex items-center gap-2.5">
            {/* Cancel — outlined ghost pill, hover pine */}
            <button
              type="button"
              onClick={() => navigate('/')}
              disabled={busy}
              className="rounded-full border border-charcoal/25 px-[22px] py-2.5 text-[13px] font-medium text-charcoal transition-colors hover:border-pine hover:text-pine disabled:opacity-50"
            >
              Cancel
            </button>
            {/* Submit — pine pill with shadow lift on hover */}
            <button
              type="submit"
              disabled={busy || overLimit}
              data-tour="new-submit"
              className="rounded-full bg-pine px-[22px] py-2.5 text-[13px] font-medium text-cloud shadow-md transition-all hover:-translate-y-0.5 hover:bg-pine-deep hover:shadow-lg disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-md"
            >
              {status === 'creating'
                ? 'Creating draft…'
                : status === 'transforming'
                  ? 'Transforming…'
                  : rawInput.trim().length > 0
                    ? 'Create draft + transform'
                    : 'Create draft'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  required,
  children,
  className,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  // Mockup .nm-field__label: 10px pine bold tracking-[0.22em]
  // uppercase, opacity-85. Required asterisk is terra-cotta (mockup
  // .required color), giving a clear "this is mandatory" cue without
  // hijacking the pine accent.
  return (
    <label className={'block ' + (className ?? '')}>
      <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.22em] text-pine/85">
        {label}
        {required && <span className="ml-0.5 text-terra-cotta">*</span>}
      </span>
      {children}
    </label>
  );
}
